import { useEffect, useMemo, useState } from "react";
import { ImageOff, Loader2, X } from "lucide-react";
import type { TechnicianAttachment } from "@myapp/contract";
import { API_URL } from "../../../config/axios";
import { describeFile } from "../../../lib/fileType";
import { formatBytes } from "../../../lib/formatBytes";
import { isStandalone } from "../../../lib/pwa";
import { DocumentViewer } from "../../overlay/DocumentViewer/DocumentViewer";
import { Lightbox, type LightboxItem } from "../../overlay/Lightbox/Lightbox";

type Props = {
  items: TechnicianAttachment[];
  /**
   * Decides per row whether the X is offered. The API enforces the same rule (uploader or office),
   * so this only keeps a button that would 403 off the screen.
   */
  canRemove?: (item: TechnicianAttachment) => boolean;
  onRemove?: (item: TechnicianAttachment) => void;
  /** The row whose removal is in flight — its X becomes a spinner and the row dims. */
  removingId?: number | null;
  emptyText?: string;
  className?: string;
};

const UPLOADED_AT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const formatUploadedAt = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : UPLOADED_AT.format(date);
};

/**
 * Files the technician sent, one row each: thumbnail or type icon, the real filename, and who
 * sent it when. Shared by the worker card (with an X on the rows the worker may remove) and the
 * office's read-only summary — the same list in both places so the two never disagree on what a
 * field file looks like.
 *
 * Opening follows `AttachmentsCard`: photos go to the in-app `Lightbox`; documents open the
 * `DocumentViewer` inside an installed PWA and a new tab otherwise.
 */
export const TechnicianAttachmentList = ({
  items,
  canRemove,
  onRemove,
  removingId = null,
  emptyText = "Noch keine Dateien",
  className,
}: Props) => {
  const rows = useMemo(
    () =>
      items.map((item) => ({
        item,
        url: `${API_URL}${item.url}`,
        descriptor: describeFile({ url: item.url, mime: item.mimeType, filename: item.originalName }),
      })),
    [items],
  );

  const [broken, setBroken] = useState<Record<number, boolean>>({});
  useEffect(() => setBroken({}), [items]);

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [viewerUrl, setViewerUrl] = useState<{ url: string; label: string } | null>(null);

  const images = rows.filter((row) => row.descriptor.kind === "image");
  const lightboxItems: LightboxItem[] = images.map((row) => ({
    key: String(row.item.id),
    url: row.url,
    label: row.item.originalName,
    meta: [row.item.uploadedBy.fullName, formatUploadedAt(row.item.uploadedAt)]
      .filter(Boolean)
      .join(" · "),
  }));

  const openInApp = isStandalone();

  if (rows.length === 0) {
    return <p className={`text-sm text-slate-400 ${className ?? ""}`}>{emptyText}</p>;
  }

  return (
    <>
      <ul className={`flex flex-col gap-2 ${className ?? ""}`}>
        {rows.map((row) => {
          const { item, url, descriptor } = row;
          const isImage = descriptor.kind === "image";
          const imageIndex = isImage ? images.indexOf(row) : -1;
          const failed = isImage && broken[item.id];
          const removing = removingId === item.id;
          const removable = Boolean(onRemove && (canRemove ? canRemove(item) : true));
          const Icon = descriptor.Icon;

          const meta = [
            item.uploadedBy.fullName,
            formatUploadedAt(item.uploadedAt),
            item.sizeBytes != null ? formatBytes(item.sizeBytes) : null,
          ]
            .filter(Boolean)
            .join(" · ");

          const preview = (
            <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white">
              {isImage && !failed ? (
                <img
                  src={url}
                  alt=""
                  loading="lazy"
                  onError={() => setBroken((prev) => ({ ...prev, [item.id]: true }))}
                  className="h-full w-full object-cover"
                />
              ) : isImage ? (
                <ImageOff size={18} className="text-slate-400" aria-hidden />
              ) : (
                <Icon size={18} className={descriptor.tint} aria-hidden />
              )}
            </span>
          );

          const text = (
            <span className="min-w-0 flex-1 text-left">
              <span className="block truncate text-sm font-medium text-slate-900">
                {item.originalName}
              </span>
              <span className="block truncate text-xs text-slate-500">{meta}</span>
            </span>
          );

          const openClass =
            "flex min-w-0 flex-1 items-center gap-3 rounded-xl text-left disabled:pointer-events-none";

          return (
            <li
              key={item.id}
              className={`flex min-h-[56px] items-center gap-2 rounded-xl bg-[#EEF1F7] px-3 py-2 ${
                removing ? "opacity-50" : ""
              }`}
            >
              {isImage ? (
                <button
                  type="button"
                  onClick={() => setLightboxIndex(imageIndex)}
                  disabled={failed}
                  className={openClass}
                >
                  {preview}
                  {text}
                </button>
              ) : openInApp ? (
                <button
                  type="button"
                  onClick={() => setViewerUrl({ url, label: item.originalName })}
                  className={openClass}
                >
                  {preview}
                  {text}
                </button>
              ) : (
                <a href={url} target="_blank" rel="noreferrer" className={openClass}>
                  {preview}
                  {text}
                </a>
              )}

              {removable && (
                <button
                  type="button"
                  onClick={() => onRemove?.(item)}
                  disabled={removing}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-400 hover:text-red-500 disabled:pointer-events-none"
                  aria-label={`Datei ${item.originalName} entfernen`}
                >
                  {removing ? (
                    <Loader2 size={16} className="animate-spin" aria-hidden />
                  ) : (
                    <X size={16} aria-hidden />
                  )}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <Lightbox
        items={lightboxItems}
        index={lightboxIndex ?? 0}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
        open={lightboxIndex !== null}
      />

      <DocumentViewer
        url={viewerUrl?.url ?? null}
        label={viewerUrl?.label ?? ""}
        onClose={() => setViewerUrl(null)}
        open={viewerUrl !== null}
      />
    </>
  );
};
